import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import { artifactKeys } from '../query-keys';
import {
  addArtifactContract,
  updateArtifactContract,
  deleteArtifactContract,
  type AddArtifactRequest,
  type UpdateArtifactRequest,
  type AddArtifactResponse,
  type UpdateArtifactResponse,
  type DeleteArtifactResponse
} from '@bliks-io/contracts';

/**
 * Create artifact mutation
 */
export function useCreateArtifact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AddArtifactRequest): Promise<AddArtifactResponse> => {
      const { data: response } = await apiClient.post<AddArtifactResponse>(
        addArtifactContract.path,
        data
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: artifactKeys.lists() });
    }
  });
}

/**
 * Update artifact mutation
 */
export function useUpdateArtifact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data
    }: {
      id: string;
      data: UpdateArtifactRequest;
    }): Promise<UpdateArtifactResponse> => {
      const path = updateArtifactContract.path.replace(':id', id);
      const { data: response } = await apiClient.patch<UpdateArtifactResponse>(path, data);
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: artifactKeys.lists() });
      queryClient.invalidateQueries({ queryKey: artifactKeys.detail(variables.id) });
    }
  });
}

/**
 * Delete artifact mutation
 */
export function useDeleteArtifact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<DeleteArtifactResponse> => {
      const path = deleteArtifactContract.path.replace(':id', id);
      const { data } = await apiClient.delete<DeleteArtifactResponse>(path);
      return data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: artifactKeys.lists() });
      queryClient.removeQueries({ queryKey: artifactKeys.detail(id) });
    }
  });
}
